import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { TimeField } from '@/components/form/TimeInput';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { handleMutationResult } from '@/helpers/toastHelper';
import { Location, useUpdateLocationMutation } from '@/services/gql/graphql.generated';
import { DayKey, DefaultShiftTimesString } from '@/services/settings/systemSettings.types';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useEffect } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { FiClock } from 'react-icons/fi';
import { shiftStringsToSeconds, shiftTimesToStrings } from '../../../../helpers/dateHelper';
import adminStrings from '../../admin.translations';
import { useLocationDefaultTimes } from './useLocationDefaultTimes';

interface DefaultTimesFormValues {
  shiftTimes: DefaultShiftTimesString;
  rosterTimes: DefaultShiftTimesString;
  openingTimes: DefaultShiftTimesString;
}

interface AdminEditLocationTimesCardProps {
  location: Location;
}

interface DayTimeRowsProps {
  prefix: string;
  getEndFieldRules?: (key: DayKey) => RegisterOptions;
}

const AdminEditLocationTimesCard: FunctionComponent<AdminEditLocationTimesCardProps> = ({ location }) => {
  const [{ fetching }, updateLocation] = useUpdateLocationMutation();

  const { defaultShiftTimes, defaultRosterTimes, defaultOpeningTimes } = useLocationDefaultTimes(location.id);

  const methods = useForm<DefaultTimesFormValues>({
    defaultValues: {
      shiftTimes: shiftTimesToStrings(defaultShiftTimes),
      rosterTimes: shiftTimesToStrings(defaultRosterTimes),
      openingTimes: shiftTimesToStrings(defaultOpeningTimes)
    }
  });

  useEffect(() => {
    methods.reset({
      shiftTimes: shiftTimesToStrings(defaultShiftTimes),
      rosterTimes: shiftTimesToStrings(defaultRosterTimes),
      openingTimes: shiftTimesToStrings(defaultOpeningTimes)
    });
  }, [defaultShiftTimes, defaultRosterTimes, defaultOpeningTimes]);

  const onSubmit = async (values: DefaultTimesFormValues) => {
    const existingProperties = JSON.parse(location.properties ?? '{}');

    const result = await updateLocation({
      location: {
        id: location.id,
        name: location.name,
        active: location.active,
        properties: JSON.stringify({
          ...existingProperties,
          defaultShiftTimes: shiftStringsToSeconds(values.shiftTimes),
          defaultRosterTimes: shiftStringsToSeconds(values.rosterTimes),
          defaultOpeningTimes: shiftStringsToSeconds(values.openingTimes)
        })
      }
    });

    await handleMutationResult(result, {
      successMessage: 'Default times updated successfully.',
      errorMessage: 'Default times updated failed.'
    });
  };

  const getEndTimeRules =
    (prefix: keyof DefaultTimesFormValues) =>
    (key: DayKey): RegisterOptions => ({
      validate: end => {
        const start = methods.getValues(`${prefix}.${key}.start`);
        return end >= start || adminStrings.locationsSection.editLocationTimes.rosterTimes.maxCannotBeLowerThanMin;
      }
    });

  const DayTimeRows = ({ prefix, getEndFieldRules }: DayTimeRowsProps) => (
    <div className="md:ml-20 w-full flex flex-col space-y-3">
      <div className="flex flex-row items-center gap-2">
        <div className="w-40" />
        <p className="w-40 text-center font-medium">{adminStrings.locationsSection.editLocationTimes.startTime}</p>
        <p className="w-40 text-center font-medium">{adminStrings.locationsSection.editLocationTimes.endTime}</p>
      </div>
      {strings.common.daysOfWeekFullStartMonday.map(day => {
        const key = day.toLowerCase() as DayKey;
        return (
          <div key={key} className="flex flex-row items-center space-x-3">
            <p className="w-40 font-medium">{day}</p>
            <TimeField className="w-40" id={`${prefix}-${key}-start`} name={`${prefix}.${key}.start`} />
            <TimeField
              className="w-40"
              id={`${prefix}-${key}-end`}
              name={`${prefix}.${key}.end`}
              rules={getEndFieldRules?.(key)}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <TSTitleCard
      title={adminStrings.locationsSection.editLocationTimes.title(location.name)}
      icon={FiClock}
      className="w-full"
    >
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="space-y-12">
            <div className="space-y-5">
              <div className="space-y-1">
                <p className="text-xl font-semibold">
                  {adminStrings.locationsSection.editLocationTimes.shiftTimes.defaultShiftTimes}
                </p>
                <div className="leading-tight">
                  <p className="text-secondary-std">
                    {adminStrings.locationsSection.editLocationTimes.shiftTimes.defaultShiftTimesDescription}
                  </p>
                </div>
              </div>
              <DayTimeRows prefix="shiftTimes" getEndFieldRules={getEndTimeRules('shiftTimes')} />
            </div>

            <div className="space-y-5">
              <div className="space-y-1">
                <p className="text-xl font-semibold">
                  {adminStrings.locationsSection.editLocationTimes.rosterTimes.defaultRosterTimes}
                </p>
                <div className="leading-tight">
                  <p className="text-secondary-std">
                    {adminStrings.locationsSection.editLocationTimes.rosterTimes.defaultRosterTimesDescription}
                  </p>
                  <p className="text-secondary-std">
                    {adminStrings.locationsSection.editLocationTimes.rosterTimes.defaultRosterTimesDescriptionPart2}
                  </p>
                </div>
              </div>
              <DayTimeRows prefix="rosterTimes" getEndFieldRules={getEndTimeRules('rosterTimes')} />
            </div>

            <div className="space-y-5">
              <div className="space-y-1">
                <p className="text-xl font-semibold">
                  {adminStrings.locationsSection.editLocationTimes.openingTimes.defaultOpeningTimes}
                </p>
                <div className="leading-tight">
                  <p className="text-secondary-std">
                    {adminStrings.locationsSection.editLocationTimes.openingTimes.defaultOpeningTimesDescription}
                  </p>
                  <p className="text-secondary-std">
                    {adminStrings.locationsSection.editLocationTimes.openingTimes.defaultOpeningTimesDescriptionPart2}
                  </p>
                </div>
              </div>
              <DayTimeRows prefix="openingTimes" getEndFieldRules={getEndTimeRules('openingTimes')} />
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-5 mt-5">
            <Button onClick={() => methods.reset()}>{strings.common.cancel}</Button>
            <Button primary loading={fetching} type="submit">
              {strings.common.save}
            </Button>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </TSTitleCard>
  );
};

export default AdminEditLocationTimesCard;
